import type { CompanyChannel } from '@tundr/auth';
import type { EntityBaseFields } from '@tundr/http';
import type { CheckableSelectItem, OptionObject } from '@tundr/ui-kit';

const CreationFlow = {
  BO: 'BO',
  RESELLER: 'RESELLER',
} as const;

type CreationFlow = (typeof CreationFlow)[keyof typeof CreationFlow];

export { CreationFlow };

export const MEAL_VOUCHER_VAT_PERCENTAGE = {
  FOUR: 4,
  TEN: 10,
} as const;

export type MealVoucherVatPercentage =
  (typeof MEAL_VOUCHER_VAT_PERCENTAGE)[keyof typeof MEAL_VOUCHER_VAT_PERCENTAGE];

type CompanyBase = EntityBaseFields & {
  name: string;
  externalAccountId: string;
  cardProductId: string;
  currentWelfarePlan: CompanyWelfarePlan;
  companyChannel: CompanyChannel;
  companyChannelId: string;
  creationFlow?: CreationFlow;
  fileRefId?: string;
  welfarePlanUpgradeDate?: string;
  welfarePlanUpgradedBy?: string;
};

export type CompanyListItem = CompanyBase & {
  isCompleted: boolean;
  hasMealVoucher: boolean;
};

export type Company = CompanyBase & {
  companyFinancialInfo?: FinancialInfo;
  externalCompanyCode?: string;
};

export type FinancialInfo = {
  id: string;
  companyId: string;
  taxId?: string;
  sdi?: string;
  email?: string;
  legalName?: string;
  address?: string;
  city?: string;
  zipCode?: string;
  contractType?: ContractTypes;
  subscriptionPrice?: number;
  contractStartDate?: string;
  contractEndDate?: string;
  physicalIssuanceCost?: number | null;
  physicalReplacementCost?: number | null;
  virtualIssuanceCost?: number | null;
  virtualReplacementCost?: number | null;
  cardIssuanceCost?: number | null;
  cardReplacementCost?: number | null;
  firstOrderShippingCost?: number | null;
  replacementShippingCost?: number | null;
  priceAdjustmentPercentage?: number | null;
  mealVouchersVatPercentage?: MealVoucherVatPercentage | null;
  splitPayment?: boolean;
  paymentTerms?: PaymentTerms;
  voucherInvoices?: VoucherInvoices;
  cig?: string;
  cup?: string;
  purchaseOrderOrRda?: string;
  contractOrOrderNumber?: string;
  contractOrOrderDate?: string;
  internalBillingNotes?: string;
  isCompleted: boolean;
};

export type TaxLegalFinancialInfoFormFields = Pick<
  Partial<FinancialInfo>,
  'taxId' | 'sdi' | 'email' | 'legalName' | 'address' | 'city' | 'zipCode'
>;

export type BillingFinancialInfoFormFields = Pick<
  Partial<FinancialInfo>,
  | 'splitPayment'
  | 'cig'
  | 'cup'
  | 'purchaseOrderOrRda'
  | 'contractOrOrderNumber'
  | 'internalBillingNotes'
> & {
  paymentTerms?: OptionObject;
  voucherInvoices?: OptionObject;
  contractOrOrderDate?: Date | string;
};

export type ContractualFinancialInfoFormFields = Pick<
  Partial<FinancialInfo>,
  | 'contractType'
  | 'subscriptionPrice'
  | 'physicalIssuanceCost'
  | 'physicalReplacementCost'
  | 'virtualIssuanceCost'
  | 'virtualReplacementCost'
  | 'cardIssuanceCost'
  | 'cardReplacementCost'
  | 'firstOrderShippingCost'
  | 'replacementShippingCost'
  | 'priceAdjustmentPercentage'
  | 'mealVouchersVatPercentage'
> & {
  contractStartDate?: Date | string;
  contractEndDate?: Date | string;
  fileRefId?: string;
};

export type FinancialInfoSectionFormFields =
  | TaxLegalFinancialInfoFormFields
  | BillingFinancialInfoFormFields
  | ContractualFinancialInfoFormFields;

export enum ContractTypes {
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
}

export enum PaymentTerms {
  DF = 'DF',
  DAYS_30 = '30',
  DAYS_60 = '60',
  DAYS_90 = '90',
  DAYS_120 = '120',
}

export enum VoucherInvoices {
  NO = 'NO',
  ON_COLLECTION = 'ON_COLLECTION',
  ADVANCE = 'ADVANCE',
  DECREASING = 'DECREASING',
}

export enum MealVoucherAdjustmentType {
  NONE = 'none',
  DISCOUNT = 'discount',
  SURCHARGE = 'surcharge',
}

/** Read-only company detail — Contract & billing tab sections (V1). */
export enum FinancialInfoSection {
  TAX_LEGAL = 'tax_legal',
  BILLING = 'billing',
  CONTRACTUAL_PRICING = 'contractual_pricing',
}

export type CompanyFormFields = {
  name: string;
  vatId?: string;
  bankProviderCardProductId: OptionObject;
  welfarePlan: OptionObject;
  fileRefId?: string;
  companyChannelId: OptionObject;
  externalCompanyCode?: string;
  boUsers?: CheckableSelectItem[];
  boUserIds?: string[];
};

export type LegacyCompanyFormFields = {
  name: string;
  bankProviderAccountId: string;
  bankProviderCardProductId: OptionObject;
  welfarePlan: OptionObject;
  fileRefId?: string;
  companyChannelId: OptionObject;
  externalCompanyCode?: string;
};

export type CreateCompanyInitialPlanSelected = {
  welfarePlan: OptionObject;
};

export enum CompanyWelfarePlan {
  STANDARD = 'STANDARD',
  COMPLETE = 'COMPLETE',
}

export type CardProducts = {
  id: string;
  name: string;
};
